import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  BarChart3,
  ChevronDown,
  Users,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Lightbulb,
  Target,
  PieChart,
} from 'lucide-angular';
import {
  GradingResultsPreview,
  GradingRunStudentResult,
  GradingResultDetail,
  QuestionAnalysis,
  GradeLetter,
} from '../../grading.types';
import { GradingApiService } from '../../services/grading-api.service';
import { StudentResultDetailsComponent } from './student-result-details/student-result-details.component';
import {
  QuestionAnalysisComponent,
  QuestionStudentFeedbackMap,
  StudentQuestionFeedback,
} from './question-analysis/question-analysis.component';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';
import { forkJoin } from 'rxjs';
import {
  GradeDistributionChartComponent,
  GradeDistributionEntry,
  buildGradeDistributionFromLetters,
} from '../../../../../common/components/grade-distribution-chart/grade-distribution-chart';

/**
 * Score tier for visual categorization
 */
type ScoreTier = 'high' | 'medium' | 'low' | 'failed';

/**
 * Use GradeLetter type from grading.types
 */
type LetterGrade = GradeLetter;

// GradeDistributionEntry is imported from the shared component

@Component({
  selector: 'app-grading-results-preview',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, StudentResultDetailsComponent, QuestionAnalysisComponent, GradeDistributionChartComponent],
  templateUrl: './grading-results-preview.component.html',
  styleUrl: './grading-results-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradingResultsPreviewComponent {
  private readonly gradingApiService = inject(GradingApiService);
  private readonly amplitudeService = inject(AmplitudeService);

  readonly preview = input.required<GradingResultsPreview>();

  protected readonly icons = {
    BarChart3,
    ChevronDown,
    Users,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Lightbulb,
    Target,
    PieChart,
  };

  // Track expanded sections - students section expanded by default
  protected readonly expandedSections = signal<Set<string>>(new Set(['students']));

  // Selected student for detail view
  protected readonly selectedStudent = signal<GradingRunStudentResult | null>(null);
  protected readonly selectedStudentDetail = signal<GradingResultDetail | null>(null);
  protected readonly isLoadingDetail = signal(false);
  protected readonly detailError = signal<string | null>(null);

  // Question analysis state (initialized from preview, updated when fetching student details)
  protected readonly questionAnalysis = signal<QuestionAnalysis | null>(null);

  // Student details for question analysis feedback
  protected readonly allStudentDetails = signal<GradingResultDetail[]>([]);
  protected readonly isLoadingStudentFeedback = signal(false);
  protected readonly studentFeedbackLoaded = signal(false);

  // Computed map of per-question student feedback for students with < A grade
  protected readonly studentFeedbackMap = computed<QuestionStudentFeedbackMap>(() => {
    const details = this.allStudentDetails();
    const students = this.preview().students;
    const map = new Map<string, StudentQuestionFeedback[]>();

    // Build a map of studentId -> studentName
    const studentNameMap = new Map<string, string>();
    for (const student of students) {
      studentNameMap.set(student.studentId, student.studentName);
    }

    // Process each student's results
    for (const detail of details) {
      const studentName = studentNameMap.get(detail.studentId) || 'Unknown Student';

      for (const section of detail.sectionEvaluations) {
        for (const item of section.itemEvaluations) {
          // Only include students with < A grade (percentage < 90)
          if (item.score.percentage < 90) {
            const feedback: StudentQuestionFeedback = {
              studentId: detail.studentId,
              studentName,
              score: {
                earnedPoints: item.score.earnedPoints,
                maxPoints: item.score.maxPoints,
                percentage: item.score.percentage,
                gradeLetter: item.score.gradeLetter,
              },
              feedback: item.feedback,
            };

            const existing = map.get(item.itemId) || [];
            existing.push(feedback);
            map.set(item.itemId, existing);
          }
        }
      }
    }

    // Sort each item's feedback by score (lowest first)
    for (const [itemId, feedbacks] of map.entries()) {
      feedbacks.sort((a, b) => a.score.percentage - b.score.percentage);
      map.set(itemId, feedbacks);
    }

    return map;
  });

  constructor() {
    // Initialize questionAnalysis from preview input when it changes
    effect(() => {
      const preview = this.preview();
      if (preview.questionAnalysis && !this.questionAnalysis()) {
        this.questionAnalysis.set(preview.questionAnalysis);
      }
    });
  }

  /**
   * Get subtitle text with summary info
   */
  protected getSubtitle(): string {
    const p = this.preview();
    if (p.failedCount > 0) {
      return `${p.completedCount}/${p.studentCount} students graded`;
    }
    return `${p.studentCount} students graded`;
  }

  /**
   * Get formatted average score
   */
  protected readonly averageDisplay = computed(() => {
    const p = this.preview();
    if (p.averagePercentage === undefined) {
      return null;
    }
    return {
      percentage: p.averagePercentage,
      minPercentage: p.minPercentage,
      maxPercentage: p.maxPercentage,
    };
  });

  /**
   * Get students sorted by score (highest first), then failed at end
   */
  protected readonly sortedStudents = computed(() => {
    const students = [...this.preview().students];
    return students.sort((a, b) => {
      // Failed students go to the end
      if (a.status === 'failed' && b.status !== 'failed') return 1;
      if (b.status === 'failed' && a.status !== 'failed') return -1;
      // Sort by percentage descending
      const aScore = a.score?.percentage ?? 0;
      const bScore = b.score?.percentage ?? 0;
      return bScore - aScore;
    });
  });

  /**
   * Check if there are any successful results
   */
  protected readonly hasSuccessfulResults = computed(() => {
    return this.preview().completedCount > 0;
  });

  /**
   * Get grade distribution for the bar chart
   */
  protected readonly gradeDistribution = computed<GradeDistributionEntry[]>(() => {
    const students = this.preview().students.filter(s => s.status !== 'failed' && s.score);

    if (students.length === 0) return [];

    const letterGrades = students.map(s => s.score!.gradeLetter);
    return buildGradeDistributionFromLetters(letterGrades);
  });

  /**
   * Toggle section expansion
   */
  protected toggleSection(sectionId: string): void {
    const current = this.expandedSections();
    const newSet = new Set(current);
    const isExpanding = !newSet.has(sectionId);

    if (isExpanding) {
      newSet.add(sectionId);

      // Fetch all student details when question analysis is expanded
      if (sectionId === 'question-analysis' && !this.studentFeedbackLoaded()) {
        this.loadAllStudentDetails();
      }
    } else {
      newSet.delete(sectionId);
    }
    this.expandedSections.set(newSet);

    this.amplitudeService.trackEvent('grading_results_section_toggled', {
      sectionId,
      isExpanded: isExpanding,
    });
  }

  /**
   * Load all student details for question analysis feedback
   */
  private loadAllStudentDetails(): void {
    const students = this.preview().students.filter(s => s.status === 'success');

    if (students.length === 0) {
      this.studentFeedbackLoaded.set(true);
      return;
    }

    this.isLoadingStudentFeedback.set(true);

    // Fetch all student details in parallel
    const requests = students.map(student =>
      this.gradingApiService.getGradingResult(student.gradingResultId)
    );

    forkJoin(requests).subscribe({
      next: (details) => {
        this.allStudentDetails.set(details);
        this.studentFeedbackLoaded.set(true);
        this.isLoadingStudentFeedback.set(false);

        // Update question analysis if available from any detail
        const detailWithAnalysis = details.find(d => d.questionAnalysis);
        if (detailWithAnalysis?.questionAnalysis) {
          this.questionAnalysis.set(detailWithAnalysis.questionAnalysis);
        }
      },
      error: (err) => {
        console.error('Failed to load student details for question analysis:', err);
        this.isLoadingStudentFeedback.set(false);
        this.studentFeedbackLoaded.set(true);
      },
    });
  }

  /**
   * Check if section is expanded
   */
  protected isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  /**
   * Get score tier for a student (for color coding)
   */
  protected getScoreTier(student: GradingRunStudentResult): ScoreTier {
    if (student.status === 'failed') return 'failed';
    const percentage = student.score?.percentage ?? 0;
    if (percentage >= 80) return 'high';
    if (percentage >= 60) return 'medium';
    return 'low';
  }

  /**
   * Get tier color classes
   */
  protected getTierColorClasses(tier: ScoreTier): string {
    switch (tier) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-orange-600';
      case 'failed':
        return 'text-red-500';
    }
  }

  /**
   * Get tier icon
   */
  protected getTierIcon(tier: ScoreTier) {
    switch (tier) {
      case 'high':
        return this.icons.CheckCircle2;
      case 'medium':
      case 'low':
        return this.icons.AlertTriangle;
      case 'failed':
        return this.icons.XCircle;
    }
  }

  /**
   * Format student score display - shows letter grade with +/- from backend
   */
  protected formatStudentScore(student: GradingRunStudentResult): string {
    if (student.status === 'failed') {
      return 'Failed';
    }
    if (student.score?.gradeLetterWithSign) {
      return student.score.gradeLetterWithSign;
    }
    return '-';
  }

  /**
   * Select a student to view details
   */
  protected selectStudent(student: GradingRunStudentResult): void {
    this.selectedStudent.set(student);
    this.selectedStudentDetail.set(null);
    this.detailError.set(null);
    this.isLoadingDetail.set(true);

    // Track student selection
    this.amplitudeService.trackEvent('grading_student_selected', {
      percentage: student.score?.percentage,
      letterGrade: student.score?.gradeLetterWithSign,
      status: student.status,
    });

    // Fetch student details from API (includes question analysis)
    this.gradingApiService.getGradingResult(student.gradingResultId).subscribe({
      next: (detail) => {
        this.selectedStudentDetail.set(detail);
        // Update question analysis from the response
        if (detail.questionAnalysis) {
          this.questionAnalysis.set(detail.questionAnalysis);
        }
        this.isLoadingDetail.set(false);
      },
      error: (err) => {
        this.detailError.set(err?.message || 'Failed to load student details');
        this.isLoadingDetail.set(false);
      },
    });
  }

  /**
   * Go back to the list view from student details
   */
  protected backToList(): void {
    this.selectedStudent.set(null);
    this.selectedStudentDetail.set(null);
    this.detailError.set(null);
  }

  /**
   * Convert a percentage to a letter grade
   */
  protected getLetterGrade(percentage: number): LetterGrade {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  }

  /**
   * Get color class for a letter grade
   */
  protected getGradeColorClass(grade: LetterGrade): string {
    switch (grade) {
      case 'A':
        return 'text-emerald-600';
      case 'B':
        return 'text-green-600';
      case 'C':
        return 'text-yellow-600';
      case 'D':
        return 'text-orange-600';
      case 'F':
        return 'text-red-600';
    }
  }
}
